import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PageType } from '@modules/editor/pages/models/content.model';

/* ====== MODELE DANYCH ====== */

export interface SelectOption<T = any> {
  value: T;
  label: string;
  name?: string;
}

export interface TagTranslation {
  locale: string;
  name: string;
}

export interface Tag {
  id: string | number;
  name?: string;
  value?: string;
  translations?: TagTranslation[];
}

/** Dozwolone struktury danych dla opcji */
export type MultiOption<T = any> =
  | SelectOption<T> // { value, label }
  | Tag // { id, translations[] | name }
  | PageType // { type, translations[] | name }
  | Record<string, Tag>; // mapa 'pl' | 'en' → Tag

/* ====== KOMPONENT ====== */

@Component({
  selector: 'as-multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AsMultiselectComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class AsMultiselectComponent<T = any>
  implements ControlValueAccessor, AfterViewInit, OnDestroy {
  /* ---------- Inputs ---------- */
  @Input() options: MultiOption<T>[] = [];
  @Input() placeholder = '— wybierz —';

  @Input() multiple = true;
  @Input() language = 'en';

  @Input()
  set disabledSet(v: boolean) {
    this.disabled = v;
  }

  disabled = false;

  @Input()
  get valueModel(): any[] {
    return this.value;
  }

  set valueModel(v: any[]) {
    this.writeValue(v);
  }

  @Output() valueModelChange = new EventEmitter<any[]>();

  /* ---------- Outputs ---------- */
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() selection = new EventEmitter<MultiOption<T>[]>();

  static ngAcceptInputType_valueModel: any;
  static ngAcceptInputType_value: any;

  @ViewChild('triggerEl', {read: ElementRef, static: true})
  triggerEl!: ElementRef<HTMLElement>;

  // 2) referencja do dyrektywy CDK
  @ViewChild('overlayOrigin', {read: CdkOverlayOrigin, static: true})
  overlayOrigin!: CdkOverlayOrigin;

  overlayWidth = 0;

  /* ---------- State ---------- */
  /** tablica ID-ków zaznaczonych elementów (string / number / Generic) */
  value: any[] = [];
  panelOpen = false;
  readonly panelId = `as-multiselect-panel-${crypto.randomUUID()}`;

  /** Wybrane obiekty (do chipsów) */
  get selectedOptions(): MultiOption<T>[] {
    let options;
    if (this.options) {
      options = this.options.filter((o) =>
        this.value.includes(this.getValue(o)),
      );
    }
    return options;
  }

  /* ---------- ControlValueAccessor ---------- */
  private onChange: (v: any) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  writeValue(val: any): void {
    this.value = Array.isArray(val) ? [...val] : val != null ? [val] : [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // setDisabledStatez(isDisabled: boolean): void {
  //   this.disabled = isDisabled;
  // }

  /* ---------- Lifecycle ---------- */
  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private elRef: ElementRef,
  ) {
  }

  ngAfterViewInit(): void {
    this.overlayWidth = this.triggerEl.nativeElement.offsetWidth;
    this.doc.addEventListener('keydown', this.docKeydown, true);
  }

  ngOnDestroy(): void {
    this.doc.removeEventListener('keydown', this.docKeydown, true);
  }

  /* ---------- Public API ---------- */
  togglePanel(): void {
    if (this.disabled) return;
    if (this.panelOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
    ;
    this.onTouched();
  }

  openPanel(): void {
    this.panelOpen = true;
    this.opened.emit();
  }

  closePanel(): void {
    if (!this.panelOpen) return;
    this.panelOpen = false;
    this.closed.emit();
  }

  toggleOption(opt: MultiOption<T>, ev?: Event): void {
    this.onChange(this.value);
    this.valueModelChange.emit(this.value);
    this.selection.emit(this.selectedOptions);

    ev?.stopPropagation();
    const id = this.getValue(opt);

    if (this.multiple) {
      const idx = this.value.indexOf(id);
      if (idx > -1) {
        this.value.splice(idx, 1);
      } else {
        this.value.push(id);
      }
    } else {
      /* single-select: ustawiamy dokładnie jeden element
         lub czyścimy, gdy klikniemy ponownie ten sam */
      this.value = this.value[0] === id ? [] : [id];
      /* fajnie zamknąć panel po wybraniu */
      this.closePanel();
    }
    this.value = [...this.value];
    this.onChange(this.value);
    this.valueModelChange.emit(this.value);
    this.selection.emit(this.selectedOptions);
  }

  removeOption(opt: MultiOption<T>, ev: Event): void {
    ev.stopPropagation();
    const id = this.getValue(opt);
    this.value = this.value.filter((v) => v !== id);
    this.onChange(this.value);
    this.selection.emit(this.selectedOptions);

    this.valueModelChange.emit(this.value);
  }

  /* ---------- Utils ---------- */

  /** stabilny klucz dla trackBy & value */
  getValue(opt: MultiOption<T>): any {
    if ('value' in opt) return opt.value;
    if ('id' in opt) return opt.id;
    return opt; // Record<string, Tag> – brak pojedynczego ID
  }

  /** etykieta wyświetlana w UI */

  getLabel(opt: MultiOption<T>): string {
    if (opt.name && typeof opt.name === 'string') {
      return opt.name;
    }
    if ('translations' in opt || 'name' in opt) {
      return this.labelFromTag(opt as Tag) ?? '—';
    }

    const map = opt as Record<string, Tag>;
    if (map[this.language]) return this.labelFromTag(map[this.language]) ?? '—';
    if (map['en']) return this.labelFromTag(map['en']) ?? '—';
    const first = Object.values(map)[0];
    return first ? this.labelFromTag(first) ?? '—' : '—';
  }

  /** nazwa z pojedynczego Tag-a */
  private labelFromTag(tag: Tag): string | undefined {
    const exact = tag.translations?.find((t) => t.locale === this.language);
    if (exact?.name) return exact.name;
    const eng = tag.translations?.find((t) => t.locale === 'en');
    if (eng?.name) return eng.name;
    const first = tag.translations?.[0]?.name;
    return first ?? tag.name;
  }

  trackByValue = (_: number, o: MultiOption<T>) => this.getValue(o);
  isSelected = (o: MultiOption<T>) => this.value.includes(this.getValue(o));

  /* ---------- Klawiatura / a11y ---------- */
  @HostListener('keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    if (this.disabled) return;
    if (ev.key === 'Enter' || ev.key === ' ') {
      this.togglePanel();
      ev.preventDefault();
    }
    if (ev.key === 'ArrowDown' && !this.panelOpen) {
      this.openPanel();
      ev.preventDefault();
    }
  }

  private docKeydown = (ev: KeyboardEvent) => {
    if (!this.panelOpen) return;
    if (ev.key === 'Escape' || (ev.altKey && ev.key === 'ArrowUp')) {
      this.closePanel();
      this.triggerEl.nativeElement.focus();
    }
  };
}
